import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  RestartAlt as RestartIcon,
  ClearAll as ClearDataIcon,
  DeleteSweep as UninstallIcon,
  Notifications as RingIcon,
  Message as BroadcastIcon,
  History as LogsIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { hexnodeApi, devicesApi } from '../services/apiClient';
import { DEVICE_TYPES } from '../config/constants';
import { useThemeSettings } from '../contexts/ThemeContext';

const HexnodeActions = ({ device, onActionComplete }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useThemeSettings();
  const [remainingCalls, setRemainingCalls] = useState(null);
  const [loadingCalls, setLoadingCalls] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  // Dialog states
  const [ringDialogOpen, setRingDialogOpen] = useState(false);
  const [broadcastDialogOpen, setBroadcastDialogOpen] = useState(false);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [message, setMessage] = useState('');
  const [qrCodeData, setQrCodeData] = useState(null);
  const [loadingQrCode, setLoadingQrCode] = useState(false);

  // Check if device is Hexnode-managed tablet
  // Support both deviceType_id and type_id field names
  const deviceTypeId = device?.deviceType_id || device?.type_id || device?.deviceType;
  const isHexnodeDevice = deviceTypeId === DEVICE_TYPES.ANDROID_TABLET;
  const possibleActions = device?.config?.possibleActions || [];

  // console.log('[HexnodeActions] Debug:', {
  //   deviceTypeId,
  //   isHexnodeDevice,
  //   ANDROID_TABLET: DEVICE_TYPES.ANDROID_TABLET,
  //   possibleActions,
  //   hasConfig: !!device?.config,
  //   device
  // });

  useEffect(() => {
    if (isHexnodeDevice && device?.id) {
      fetchRemainingCalls();
    }
  }, [device?.id, isHexnodeDevice]);

  const fetchRemainingCalls = async () => {
    try {
      setLoadingCalls(true);
      const response = await hexnodeApi.getRemainingCalls(device.id);
      if (response.data.success) {
        setRemainingCalls(response.data.remainingCalls);
      }
    } catch (error) {
      console.error('Error fetching remaining API calls:', error);
    } finally {
      setLoadingCalls(false);
    }
  };

  const fetchQrCodeFromDevice12 = async () => {
    try {
      setLoadingQrCode(true);

      // Get location ID - support multiple field names
      const locationId = device.location_id ||
                        device.locationId ||
                        device.location ||
                        device.deviceLocation ||
                        device.locationModel?.id ||
                        device.locationModel?._id;

      console.log('[HexnodeActions] Fetching QR code - device:', device);
      console.log('[HexnodeActions] Location ID:', locationId);

      if (!locationId) {
        console.error('[HexnodeActions] No location ID found for device');
        setQrCodeData(null);
        return;
      }

      // Fetch all devices at the same location
      const response = await devicesApi.getByLocation(locationId);

      if (response.data.success && Array.isArray(response.data.data)) {
        console.log('[HexnodeActions] Devices at location:', response.data.data);

        // Find device with deviceType=12 at same location
        const device12 = response.data.data.find(d => d.deviceType === 12);
        console.log('[HexnodeActions] Device type 12:', device12);

        if (device12 && device12.config?.api?.qr) {
          setQrCodeData(device12.config.api.qr);
          console.log('[HexnodeActions] QR code found');
        } else {
          setQrCodeData(null);
          console.log('[HexnodeActions] No device type 12 with QR code found');
        }
      }
    } catch (error) {
      console.error('Error fetching QR code from device type 12:', error);
      setQrCodeData(null);
    } finally {
      setLoadingQrCode(false);
    }
  };

  const handleAction = async (actionFn, actionName) => {
    if (remainingCalls === 0) {
      enqueueSnackbar('Daily API call limit reached. Limit resets at midnight.', { variant: 'warning' });
      return;
    }

    try {
      setActionLoading(true);
      const response = await actionFn();

      if (response.data.success) {
        enqueueSnackbar(response.data.message || `${actionName} successful`, { variant: 'success' });
        setRemainingCalls(response.data.remainingApiCalls);
        if (onActionComplete) onActionComplete();
      } else {
        enqueueSnackbar(response.data.error || `${actionName} failed`, { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(error.response?.data?.error || `Failed to ${actionName}`, { variant: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestartDevice = () => {
    setPendingAction({
      name: t ? t('HexnodeActions.restart_device') : 'Restart Device',
      description: t ? t('HexnodeActions.restart_device_description') : 'This will restart the device immediately. The device will be unavailable for a few minutes.',
      action: () => handleAction(() => hexnodeApi.restartDevice(device.id), 'Device restart')
    });
    setWarningDialogOpen(true);
  };

  const handleClearData = () => {
    setPendingAction({
      name: t ? t('HexnodeActions.clear_app_data') : 'Clear App Data',
      description: t ? t('HexnodeActions.clear_app_data_description') : 'This will clear all app data and settings. The device will need to be reconfigured using the QR code.',
      action: async () => {
        await handleAction(() => hexnodeApi.clearAppData(device.id, 'com.caro.app'), 'Clear app data');
        // Fetch QR code from device type 12 and show dialog
        await fetchQrCodeFromDevice12();
        setQrDialogOpen(true);
      }
    });
    setWarningDialogOpen(true);
  };

  const handleConfirmAction = () => {
    setWarningDialogOpen(false);
    if (pendingAction?.action) {
      pendingAction.action();
    }
    setPendingAction(null);
  };

  const handleCancelAction = () => {
    setWarningDialogOpen(false);
    setPendingAction(null);
  };

  const handleRingDevice = async () => {
    if (!message.trim()) {
      enqueueSnackbar('Please enter a message', { variant: 'warning' });
      return;
    }

    await handleAction(() => hexnodeApi.ringDevice(device.id, message), 'Ring device');
    setRingDialogOpen(false);
    setMessage('');
  };

  const handleBroadcastMessage = async () => {
    if (!message.trim()) {
      enqueueSnackbar('Please enter a message', { variant: 'warning' });
      return;
    }

    await handleAction(() => hexnodeApi.broadcastMessage(device.id, message), 'Broadcast message');
    setBroadcastDialogOpen(false);
    setMessage('');
  };

  if (!isHexnodeDevice) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {/* API Calls Remaining Chip */}
      {loadingCalls ? (
        <Chip
          size="small"
          icon={<CircularProgress size={16} />}
          label="Loading..."
          variant="outlined"
        />
      ) : remainingCalls !== null && (
        <Chip
          size="small"
          label={`${remainingCalls}/10 API calls today`}
          color={remainingCalls > 5 ? 'success' : remainingCalls > 2 ? 'warning' : 'error'}
          variant="outlined"
        />
      )}

      {/* Restart Device */}
      {possibleActions.includes('restart') && (
        <Tooltip title={remainingCalls === 0 ? 'API limit reached' : 'Restart Device'}>
          <span>
            <IconButton
              color="warning"
              onClick={handleRestartDevice}
              disabled={actionLoading || remainingCalls === 0}
              sx={{
                minWidth: 44,
                minHeight: 44,
                bgcolor: 'warning.main',
                color: 'white',
                '&:hover': { bgcolor: 'warning.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
              }}
            >
              {actionLoading ? <CircularProgress size={24} color="inherit" /> : <RestartIcon />}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Clear App Data */}
      {possibleActions.includes('clearData') && (
        <Tooltip title={remainingCalls === 0 ? 'API limit reached' : 'Clear App Data'}>
          <span>
            <IconButton
              color="info"
              onClick={handleClearData}
              disabled={actionLoading || remainingCalls === 0}
              sx={{
                minWidth: 44,
                minHeight: 44,
                bgcolor: 'info.main',
                color: 'white',
                '&:hover': { bgcolor: 'info.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
              }}
            >
              {actionLoading ? <CircularProgress size={24} color="inherit" /> : <ClearDataIcon />}
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Ring Device */}
      {possibleActions.includes('ring') && (
        <Tooltip title={remainingCalls === 0 ? 'API limit reached' : 'Ring Device'}>
          <span>
            <IconButton
              color="secondary"
              onClick={() => setRingDialogOpen(true)}
              disabled={actionLoading || remainingCalls === 0}
              sx={{
                minWidth: 44,
                minHeight: 44,
                bgcolor: 'secondary.main',
                color: 'white',
                '&:hover': { bgcolor: 'secondary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
              }}
            >
              <RingIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Broadcast Message / Send Message */}
      {(possibleActions.includes('broadcast') || possibleActions.includes('sendMessage')) && (
        <Tooltip title={remainingCalls === 0 ? 'API limit reached' : 'Send Message'}>
          <span>
            <IconButton
              color="primary"
              onClick={() => setBroadcastDialogOpen(true)}
              disabled={actionLoading || remainingCalls === 0}
              sx={{
                minWidth: 44,
                minHeight: 44,
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
              }}
            >
              <BroadcastIcon />
            </IconButton>
          </span>
        </Tooltip>
      )}

      {/* Ring Device Dialog */}
      <Dialog open={ringDialogOpen} onClose={() => setRingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Ring Device</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a ring notification to the device with an optional message.
          </Typography>
          <TextField
            autoFocus
            label="Message (optional)"
            fullWidth
            multiline
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Please check the tablet"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRingDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRingDevice} variant="contained" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Ring Device'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Broadcast Message Dialog */}
      <Dialog open={broadcastDialogOpen} onClose={() => setBroadcastDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Broadcast Message</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a notification message to the device.
          </Typography>
          <TextField
            autoFocus
            label="Message"
            fullWidth
            required
            multiline
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your message here"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBroadcastDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBroadcastMessage} variant="contained" disabled={actionLoading || !message.trim()}>
            {actionLoading ? <CircularProgress size={24} /> : 'Send Message'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Warning Confirmation Dialog */}
      <Dialog open={warningDialogOpen} onClose={handleCancelAction} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Alert severity="warning" sx={{ py: 0, '& .MuiAlert-icon': { mr: 1 } }}>
              {pendingAction?.name || (t ? t('HexnodeActions.confirm_action') : 'Confirm Action')}
            </Alert>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 2 }}>
            {pendingAction?.description}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t ? t('HexnodeActions.are_you_sure') : 'Are you sure you want to continue?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelAction}>{t ? t('HexnodeActions.cancel') : 'Cancel'}</Button>
          <Button onClick={handleConfirmAction} variant="contained" color="warning">
            {t ? t('HexnodeActions.confirm') : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => setQrDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t ? t('HexnodeActions.qr_code_title') : 'Device Configuration QR Code'}</DialogTitle>
        <DialogContent>
          {loadingQrCode ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : qrCodeData ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t ? t('HexnodeActions.qr_code_instruction') : 'Scan this QR code with the device to reconfigure it after clearing app data.'}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <img
                  src={qrCodeData}
                  alt="Device Configuration QR Code"
                  style={{ maxWidth: '100%', height: 'auto', border: '1px solid #ddd', borderRadius: 4 }}
                />
              </Box>
            </>
          ) : (
            <Alert severity="error">
              {t ? t('HexnodeActions.qr_code_not_found') : 'No device with type 12 found at this location. A device of type 12 is required to provide the QR code for reconfiguration.'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQrDialogOpen(false)} variant="contained">
            {t ? t('HexnodeActions.close') : 'Close'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HexnodeActions;
